import { motion } from "framer-motion";
import {
  Check,
  ScrollText,
  Sparkles,
  UserCheck,
  ShoppingBag,
  RefreshCw,
  Star,
  Rocket,
  Award,
  Zap,
  ShieldCheck,
  Linkedin,
  MapPin,
  LogIn,
  Link2,
  FileCheck2,
  Download,
  Copy,
  Lock,
  ArrowRight,
  Timer,
} from "lucide-react";
import CTAButton from "@/components/funnel/CTAButton";
import { LANDING } from "@/constants/testIds";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_seller-rescue-funnel/artifacts/ko75qk3j_1770000143925.png";

// ────────────────────────────────────────────────────────────────────────────
// Reusable browser-window chrome for product mockups
// ────────────────────────────────────────────────────────────────────────────
function BrowserFrame({ url = "app.protegoos.com", children, className = "" }) {
  return (
    <div
      className={`bg-[#0F1013] border border-white/10 rounded-xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] ${className}`}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#0A0A0B] border-b border-white/10">
        <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <div className="flex-1 flex justify-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[10px] text-white/50 font-mono">
            <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
            {url}
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5 min-h-[220px]">{children}</div>
    </div>
  );
}

function ScreenLogin() {
  return (
    <div className="flex flex-col items-center text-center py-3">
      <p className="text-lg font-black tracking-tighter text-white mb-1">
        Protego <span className="text-[#F04632]">AI</span>
      </p>
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-5">
        Reinstatement Dashboard
      </p>
      <div className="w-full space-y-2.5 mb-4">
        <div className="w-full h-8 bg-white/[0.04] border border-white/10 rounded-md flex items-center px-3 text-xs text-white/40 font-mono">
          you@yourstore.com
        </div>
        <div className="w-full h-8 bg-white/[0.04] border border-white/10 rounded-md flex items-center px-3 text-xs text-white/40 font-mono tracking-widest">
          ••••••••••••
        </div>
      </div>
      <div className="w-full h-9 bg-[#F04632] rounded-md flex items-center justify-center gap-1.5 text-white text-xs font-bold uppercase tracking-widest">
        <LogIn className="w-3.5 h-3.5" strokeWidth={2.5} />
        Log in
      </div>
      <p className="text-[10px] text-white/40 mt-3">
        Access sent to your inbox after checkout
      </p>
    </div>
  );
}

function ScreenIntake() {
  const options = [
    { l: "Section 3 — Related account", active: true },
    { l: "Inauthentic complaint" },
    { l: "IP infringement claim" },
    { l: "Safety / product quality" },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
          Step 04 of 12
        </p>
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30">
          <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
          <span className="text-[9px] uppercase tracking-widest text-[#10B981] font-bold">
            SP-API Connected
          </span>
        </div>
      </div>
      <div className="w-full h-1 bg-white/10 rounded-full mb-4 overflow-hidden">
        <div className="h-full w-[33%] bg-[#F04632]" />
      </div>
      <p className="text-sm font-bold text-white leading-snug mb-3">
        What was your deactivation reason?
      </p>
      <div className="space-y-1.5">
        {options.map((o, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${
              o.active
                ? "bg-[#F04632]/10 border border-[#F04632]/40 text-white"
                : "bg-white/[0.03] border border-white/10 text-white/60"
            }`}
          >
            <div
              className={`w-3.5 h-3.5 rounded-full border-2 ${
                o.active ? "border-[#F04632] bg-[#F04632]" : "border-white/30"
              } flex items-center justify-center flex-shrink-0`}
            >
              {o.active && <div className="w-1 h-1 rounded-full bg-white" />}
            </div>
            <span>{o.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScreenPOA() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-full bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center">
          <Check className="w-3.5 h-3.5 text-[#10B981]" strokeWidth={3} />
        </div>
        <p className="text-[10px] uppercase tracking-widest text-[#10B981] font-bold leading-none">
          Ready · Just now
        </p>
      </div>
      <p className="text-sm font-bold text-white leading-tight mb-3">
        Your Plan of Action is ready to submit.
      </p>
      <div className="bg-[#0A0A0B] border border-white/10 rounded-md p-3 mb-3 relative overflow-hidden">
        <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold mb-1.5">
          POA_case_A47.pdf
        </p>
        <div className="space-y-1">
          <div className="h-1.5 rounded bg-white/25 w-[92%]" />
          <div className="h-1.5 rounded bg-white/15 w-[76%]" />
          <div className="h-1.5 rounded bg-white/15 w-[88%]" />
          <div className="h-1.5 rounded bg-white/10 w-[62%]" />
          <div className="h-1.5 rounded bg-white/15 w-[80%]" />
          <div className="h-1.5 rounded bg-white/10 w-[54%]" />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[#0A0A0B] to-transparent" />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div className="h-8 rounded-md bg-[#F04632] flex items-center justify-center gap-1.5 text-white text-[10px] font-bold uppercase tracking-widest">
          <Download className="w-3 h-3" strokeWidth={2.5} />
          PDF
        </div>
        <div className="h-8 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center gap-1.5 text-white/80 text-[10px] font-bold uppercase tracking-widest">
          <Copy className="w-3 h-3" strokeWidth={2.5} />
          Copy
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HOW IT WORKS — Illustrated 3-step product flow
// ────────────────────────────────────────────────────────────────────────────
export function HowItWorks() {
  const steps = [
    {
      n: "01",
      icon: LogIn,
      title: "Log in to Protego",
      body: "Pay the $197, and you get instant access to your Protego AI dashboard. No sales call, no scheduling, no back-and-forth. Access details land in your inbox the second the payment clears.",
      timing: "~60 seconds",
      screen: <ScreenLogin />,
      testId: "step-login",
    },
    {
      n: "02",
      icon: Link2,
      title: "Connect + answer 12 questions",
      body: "Grant read-only Amazon SP-API access (same one accountants use) and answer a 12-question intake. Everything Protego AI needs to reverse-engineer your root cause — most fields auto-pre-fill from your Seller Central data.",
      timing: "~8 minutes",
      screen: <ScreenIntake />,
      testId: "step-intake",
    },
    {
      n: "03",
      icon: FileCheck2,
      title: "Get your POA in your dashboard",
      body: "Hit generate. Your custom Plan of Action appears in your dashboard within minutes — as a downloadable PDF, copy-paste text, and a Google Doc link. Ready to drop straight into Amazon&apos;s appeal form.",
      timing: "Minutes",
      screen: <ScreenPOA />,
      testId: "step-poa",
    },
  ];

  return (
    <section
      data-testid={LANDING.howItWorks}
      className="relative py-20 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(240,70,50,0.12),transparent_55%)] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            The Protego Dashboard · How it works
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            From deactivated to submission-ready in{" "}
            <span className="text-[#F04632]">under 10 minutes</span>.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            No calls. No agencies. No 6-week onboarding. Log in, connect, answer, submit —
            entirely inside the Protego AI dashboard.
          </p>
        </div>

        <div className="relative grid md:grid-cols-3 gap-6 lg:gap-10">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative"
            >
              {i < steps.length - 1 && (
                <div className="hidden md:flex absolute -right-3 lg:-right-6 top-14 z-10 items-center justify-center">
                  <div className="w-9 h-9 rounded-full bg-[#F04632] flex items-center justify-center shadow-[0_0_20px_rgba(240,70,50,0.6)]">
                    <ArrowRight className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                </div>
              )}

              <div className="bg-[#121316] border border-white/10 rounded-2xl p-6 hover:border-[#F04632]/30 transition-colors h-full flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <span className="text-5xl font-black tracking-tighter text-white/10 leading-none">
                    {s.n}
                  </span>
                  <div className="w-11 h-11 rounded-lg bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#F04632]" strokeWidth={2.5} />
                  </div>
                </div>

                <div className="mb-5">
                  <BrowserFrame url="app.protegoos.com">{s.screen}</BrowserFrame>
                </div>

                <h3 className="text-xl font-black tracking-tight text-white mb-2 leading-tight">
                  {s.title}
                </h3>
                <p
                  className="text-white/60 leading-relaxed text-sm mb-5 flex-1"
                  dangerouslySetInnerHTML={{ __html: s.body }}
                />
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 w-fit">
                  <Timer className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={2.5} />
                  <span className="text-xs font-black uppercase tracking-widest text-white">
                    {s.timing}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 bg-gradient-to-b from-[#1A1C23] to-[#0F1013] border border-[#F04632]/30 rounded-2xl p-6 md:p-8 shadow-[0_0_40px_rgba(240,70,50,0.12)]"
        >
          <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#F04632] flex items-center justify-center shadow-[0_0_20px_rgba(240,70,50,0.5)]">
                <Timer className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/50 font-bold">
                  End-to-end
                </p>
                <p className="text-2xl md:text-3xl font-black tracking-tighter text-white leading-none">
                  Under 10 minutes
                </p>
              </div>
            </div>

            <p className="text-white/70 leading-relaxed text-sm sm:text-base">
              Not 24 hours. Not 3 days. Not &quot;we&apos;ll email you when it&apos;s ready.&quot;
              The whole thing runs inside your dashboard — you stay in the driver&apos;s seat
              the entire time.
            </p>

            <CTAButton testId={LANDING.howCta} size="sm">
              Start Now — $197
            </CTAButton>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FOUNDER / AUTHORITY
// ────────────────────────────────────────────────────────────────────────────
export function Founder() {
  return (
    <section
      data-testid={LANDING.founder}
      className="relative py-20 md:py-32 border-t border-white/5 bg-[#121316]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[420px_1fr] gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-white/10 shadow-2xl">
              <img
                src={FOUNDER_IMG}
                alt="Haroon Naseer — Founder of Protego AI / Section3.ai"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/85 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-white font-black text-xl tracking-tight leading-tight">
                  Haroon Naseer
                </p>
                <p className="text-white/70 text-sm mb-3">
                  Founder · Protego AI / Section3.ai
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href="https://www.linkedin.com/in/iamharoon/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-linkedin-link"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm transition-colors text-xs font-bold uppercase tracking-widest text-white"
                  >
                    <Linkedin className="w-3.5 h-3.5" strokeWidth={2.5} />
                    LinkedIn
                  </a>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-white/80">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
                    Canada
                  </span>
                </div>
              </div>
            </div>

            {/* Stat block */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { v: "2,000+", l: "Seller accounts helped" },
                { v: "10 yrs", l: "In the trenches" },
                { v: "$197", l: "Flat, one-time" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-[#0F1013] border border-white/10 rounded-xl p-3 text-center"
                >
                  <p className="text-2xl font-black tracking-tighter text-[#F04632] leading-none">
                    {s.v}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-white/50 mt-2 font-bold leading-tight">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Letter */}
          <div>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
              Who&apos;s actually building this
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-8">
              I&apos;m not an attorney. I&apos;m the operator who&apos;s spent 10 years{" "}
              <span className="text-[#F04632]">rescuing 2,000+ Amazon accounts</span>.
            </h2>

            <div className="space-y-5 text-white/75 text-lg leading-relaxed">
              <p>
                Hi — <strong className="text-white">I&apos;m Haroon Naseer.</strong> For the
                past <strong className="text-white">10 years</strong>, I&apos;ve worked with{" "}
                <strong className="text-white">over 2,000 Amazon seller accounts</strong> on
                Plans of Action, Section 3 reinstatements, and account-health emergencies. If
                Amazon has flagged, warned, deactivated, or suspended it — I&apos;ve
                probably fixed it before.
              </p>
              <p>
                In those 10 years I watched something happen over and over. A seller wakes up
                to a deactivation email, panics, and pays an &quot;Amazon attorney&quot;
                $5,000–$15,000 for a POA. Or hires a reinstatement mill for $1,500 that hands
                back a template written for 400 other people that month. And most of the time
                — they still don&apos;t get reinstated.
              </p>
              <p>
                It&apos;s the biggest legally-permitted rip-off in e-commerce, and it made me
                angry enough to build a solution.
              </p>
              <p>
                So I built <strong className="text-white">Protego AI</strong> — the reinstatement
                engine behind Section3.ai. It reads your Seller Central data, cross-references
                it against thousands of real approvals and rejections I&apos;ve personally
                worked on, and drafts your POA in the exact 5-part structure Amazon
                investigators are trained to look for.
              </p>
              <p>
                Then <strong className="text-white">I personally review every single POA</strong>{" "}
                before it goes out the door. AI does the pattern-matching. I do the judgment
                call. Nothing gets delivered without my signature on it — same standard I&apos;d
                use if you were paying me $5,000.
              </p>
              <p className="text-white font-semibold">
                I charge $197 because I&apos;d rather help 10,000 sellers a year at $197 than 200
                at $5,000. If we do this right, you&apos;ll remember Protego next time you need
                someone in your corner — that&apos;s the whole business model.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-2xl font-black tracking-tight text-white mb-1">
                — Haroon Naseer
              </p>
              <p className="text-white/50 text-sm">
                Founder, Protego AI · Section3.ai · Operating from Canada · Reviewing intake
                tickets right now.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// WHO IS THIS FOR — 3 personas
// ────────────────────────────────────────────────────────────────────────────
export function Personas() {
  const p = [
    {
      tag: "Persona 1",
      icon: RefreshCw,
      title: "You just got deactivated for the first time — and you have a 17-day clock.",
      body: "You&apos;ve never written a POA before. You&apos;ve been Googling since 6 AM. Every article contradicts the last one. You have inventory sitting in FBA and cash sitting in your reserve — and you&apos;re one botched submission away from a permanent ban. You need a real POA, right now, from people who actually know what Amazon investigators look for.",
    },
    {
      tag: "Persona 2",
      icon: ScrollText,
      title: "You already submitted a POA — and it got rejected.",
      body: "This is the danger zone. You typically only get 2–3 shots before Amazon closes the case permanently. Your second submission cannot be a copy-paste of your first with a few new sentences. It has to be structurally different, address a deeper root cause, and prove measurable preventative measures. That&apos;s exactly what Protego AI is built to draft.",
    },
    {
      tag: "Persona 3",
      icon: ShieldCheck,
      title: "You&apos;re under Section 3 review — pre-deactivation.",
      body: "Amazon has warned you. Your account isn&apos;t deactivated yet, but the enforcement email came through. Sellers who submit a proactive Plan of Action before deactivation have a dramatically higher approval rate. This is the cheapest, fastest window you&apos;ll ever have to fix the problem.",
    },
  ];

  return (
    <section
      data-testid={LANDING.personas}
      className="relative py-20 md:py-32 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            Is this actually for you?
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            The $197 POA is built for one of three specific situations.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Read them honestly. If none of these is you — save your $197. If one of them is you,
            this is the most important $197 you&apos;ll spend this year.
          </p>
        </div>

        <div className="space-y-5">
          {p.map((persona, i) => (
            <motion.div
              key={persona.tag}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="grid md:grid-cols-[120px_1fr] gap-5 md:gap-8 items-start bg-[#121316] border border-white/10 rounded-xl p-6 md:p-8 hover:border-[#F04632]/40 transition-colors"
            >
              <div className="flex md:flex-col items-center md:items-start gap-3">
                <div className="w-14 h-14 rounded-full bg-[#F04632]/10 border-2 border-[#F04632]/40 flex items-center justify-center">
                  <persona.icon className="w-6 h-6 text-[#F04632]" strokeWidth={2.5} />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-black">
                  {persona.tag}
                </p>
              </div>
              <div>
                <h3
                  className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3 leading-tight"
                  dangerouslySetInnerHTML={{ __html: persona.title }}
                />
                <p
                  className="text-white/70 leading-relaxed text-base sm:text-lg"
                  dangerouslySetInnerHTML={{ __html: persona.body }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 max-w-3xl mx-auto text-center bg-[#F04632]/5 border border-[#F04632]/20 rounded-2xl p-6 md:p-10">
          <p className="text-xl sm:text-2xl font-bold text-white mb-4 leading-tight">
            If <em>any</em> of those three is you — this is the most important $197 you&apos;ll
            spend this year.
          </p>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            If none of them is you (say, you&apos;ve never sold on Amazon, or you&apos;re
            hunting policy advice for a friend) — save your money. This isn&apos;t for you.
          </p>
          <CTAButton testId={LANDING.personasCta}>
            That&apos;s Me — Start My POA
          </CTAButton>
        </div>
      </div>
    </section>
  );
}
